import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { dropsAhead, DROP_SCORE_MIN } from '../track-analysis.js';

// Countdown - un afficheur à LED géant qui montre UN SEUL NOMBRE : combien de
// temps il reste avant le prochain drop.
//
// UN NOMBRE, UNE SIGNIFICATION, TOUJOURS LA MÊME. La première version affichait
// le numéro de mesure dans la phrase et le numéro de temps dans la mesure -
// deux petits nombres exacts, que seul quelqu'un qui sait ce qu'est une mesure
// pouvait lire. Le critère de ce visualiseur est qu'un spectateur qui ne le
// sait pas comprenne en cinq secondes : « ce nombre descend, et quand il
// arrive à zéro il se passe quelque chose. » Un décompte fait ça tout seul ;
// deux compteurs cycliques ne le font pas, quelle que soit leur exactitude.
//
// CE VISUALISEUR PRÉFÈRE NE PAS EXISTER. C'est sa seule règle vraiment
// importante, et elle est appliquée ailleurs qu'ici : orchestrator.js
// (requirementFailure) refuse de le proposer sans analyse, sans grille de temps
// mesurée, sans drop fort devant, ou si le prochain drop est à moins de quatre
// secondes ; adaptive-party.js le relit à chaque image contre le titulaire et
// l'évince au changement de morceau sans attendre la fin de sa rotation. Il n'y
// a donc AUCUN repli ici : le mode compte à rebours n'a pas de branche « sans
// analyse », parce que cette branche mentirait.
//
// Ce qu'il fait quand même sans analyse : il compte. Le mode normal - numéro de
// mesure, numéro de temps - n'a besoin que d'une phase de temps, et la console
// VDJ comme la vignette peuvent l'imposer. Ce n'est pas un compte à rebours
// dégradé, c'est l'autre moitié du visualiseur, celle qui ne promet rien.
//
// LE DÉCOMPTE EST CALÉ SUR beat_times_ms, jamais sur une extrapolation depuis
// le bpm. C'est toute la différence entre « à peu près huit temps » et « le 1
// tombe sur le dernier temps avant le drop, et l'explosion à l'instant exact ».
// profile.analysisTimeline.lookupBeatPhase() rend l'indice de temps réel à une
// position donnée ; la soustraction de deux indices est le décompte.

const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'countdown',
  family: 'graphic',
  danceability: 0.88,
  energyLevel: 0.86,
  bpmMin: 115,
  bpmMax: 180,
  movement: 0.55,
  tonality: 0.40,
  prefers: { gridStrength: [0.70, 1.0], percussiveness: [0.50, 1.0] },
  // LA CONDITION D'ÉLIGIBILITÉ. Voir orchestrator.js, requirementFailure :
  // c'est la seule exclusion du fichier qui ne se relâche jamais.
  requires: {
    analysis: true,
    drops: 1,
    beatGrid: true,
    // EN TEMPS, PAS EN SECONDES (29/09/2026 : « 12 beats maximum, not 12
    // seconds »). Le nombre affiché est un nombre de temps : le compte part à
    // 12 au plus, et jamais sous 8 - la phase dramatique compte les 8 derniers,
    // un compte plus court n'aurait plus de phase calme. Déclaré ici plutôt
    // qu'en dur dans la porte, pour que la règle se lise avec le reste du
    // profil ; track-analysis.js (COUNTDOWN_MAX_BEATS) et adaptive-party.js
    // s'y alignent.
    minBeatsUntilNextDrop: 8,
    // RÉSERVÉ AU DROP LE PLUS INTENSE DU MORCEAU (27/09/2026, demande
    // utilisateur) : jamais tiré au hasard dans le vivier. adaptive-party.js le
    // lance lui-même, une seule fois par morceau, vers ce seul drop
    // (track-analysis.js, countdownTarget), et JAMAIS plus de 12 temps avant.
    dropTrigger: true,
    maxBeatsUntilNextDrop: 12
  },
  requiresTier: 0,
  enabled: true
};

// --- LE PANNEAU ------------------------------------------------------------
// 32 lignes toujours, et autant de colonnes qu'il en faut pour que les points
// soient CARRÉS. Le brief dit 96 x 32 : c'est exactement ce que ça donne sur un
// cadre de rapport 3, c'est-à-dire les dalles du Pi (1280 x 400, rapport 3.2).
// Sur une télévision 16:9 la même règle donne 57 colonnes. L'alternative -
// imposer 96 colonnes partout - donnerait des points une fois et demie plus
// hauts que larges sur la TV, et un panneau de LED à points rectangulaires ne
// ressemble plus à un panneau de LED. Le nombre qui compte est la GÉOMÉTRIE du
// point, pas le compte de colonnes.
const LIGNES = 32;
const COLONNES_MIN = 40, COLONNES_MAX = 128;
const MAX_POINTS = COLONNES_MAX * LIGNES;

// Le point lui-même, en fraction de sa case.
const RAYON_POINT = 0.30;
// « UN HALO COURT », et c'est une contrainte de coût autant que de dessin. Le
// halo fixe la taille du quad de chaque point, et le quad fixe le remplissage :
// à 1.05 case de rayon, les 3264 points du panneau dessinaient 2,2 millions de
// fragments par image sur un écran qui en compte 512 000 - quatre fois l'écran,
// en mélange additif, mesuré à 22,5 ms sur le Pi pour un budget de 16,7. À 0.62
// le halo reste un halo (le profil est cubique : à 0.5 case il ne vaut déjà que
// 14% de son centre) et la surface tombe de moitié.
const RAYON_HALO = 0.62;
// ET LE QUAD D'UN POINT ÉTEINT N'A PAS BESOIN DE PLACE POUR UN HALO : un point
// éteint n'éclaire rien, il lui suffit de contenir son disque. Comme la grande
// majorité des points est éteinte à toute image, c'est cette ligne-là qui fait
// l'essentiel de l'économie.
const QUAD_ETEINT = 0.80;
const HALO_GAIN_MIN = 0.18, HALO_GAIN_MAX = 0.55;   // piloté par bands.low
// Un point éteint reste visible. C'est ce qui fait que le panneau EXISTE quand
// il n'affiche rien - sans ça, un afficheur qui ne dit rien est un écran noir.
const ETEINT = 0.055;
// Étalonné sur la sortie, pas sur le linéaire : voir poster.js pour le détail.
// Un 0.055 linéaire ressort autour de 25 sur 255, soit 10% - « très sombre ».
const CRETE_CHIFFRE = 0.92;   // EN LUMINANCE : c'est ce que la passe de bloom seuille
const CRETE_PANNEAU = 0.40;   // au canal maximal, qui la majore : ne bloome pas

// --- LA POLICE -------------------------------------------------------------
// Une bitmap 5 x 8, la forme classique d'un afficheur. Les coins arrondis du
// brief viennent de la MATIÈRE et non du dessin : une matrice de disques n'a
// pas d'angle vif, quelle que soit la lettre qu'on y allume.
const CHIFFRES = [
  [0x0E, 0x11, 0x13, 0x15, 0x19, 0x11, 0x11, 0x0E], // 0
  [0x04, 0x0C, 0x04, 0x04, 0x04, 0x04, 0x04, 0x0E], // 1
  [0x0E, 0x11, 0x01, 0x02, 0x04, 0x08, 0x10, 0x1F], // 2
  [0x1F, 0x02, 0x04, 0x02, 0x01, 0x11, 0x11, 0x0E], // 3
  [0x02, 0x06, 0x0A, 0x12, 0x1F, 0x02, 0x02, 0x02], // 4
  [0x1F, 0x10, 0x1E, 0x01, 0x01, 0x11, 0x11, 0x0E], // 5
  [0x06, 0x08, 0x10, 0x1E, 0x11, 0x11, 0x11, 0x0E], // 6
  [0x1F, 0x01, 0x02, 0x04, 0x08, 0x08, 0x08, 0x08], // 7
  [0x0E, 0x11, 0x11, 0x0E, 0x11, 0x11, 0x11, 0x0E], // 8
  [0x0E, 0x11, 0x11, 0x11, 0x0F, 0x01, 0x02, 0x0C]  // 9
];
// Les deux-points du format mm:ss. Deux points, pas un caractère de 5 de large :
// un afficheur ne met pas la même chasse à « : » qu'à « 8 ».
const DEUX_POINTS = [0x00, 0x00, 0x04, 0x00, 0x00, 0x04, 0x00, 0x00];
const DEUX_POINTS_L = 3;
const GLYPHE_L = 5, GLYPHE_H = 8;
const GLYPHE_ECART = 1;         // en cases de police, avant mise à l'échelle

// Un glyphe : ses bits, et sa largeur. Tout le reste du dessin passe par là.
function glyphe(car) {
  if (car === ':') return { bits: DEUX_POINTS, large: DEUX_POINTS_L };
  const n = car.charCodeAt(0) - 48;
  return { bits: CHIFFRES[n >= 0 && n <= 9 ? n : 0], large: GLYPHE_L };
}

// --- LE RYTHME -------------------------------------------------------------
// Le claquement : extinction franche puis rallumage avec un punch, exactement
// la coupe dure de poster.js. Aucun fondu nulle part, c'est un critère.
const CLAQUE_NOIR_MS = 40;
const CLAQUE_PUNCH_MS = 90;
const CLAQUE_PUNCH = 1.30;
const FLASH_MESURE_DRAMATIQUE = 0.30;
const FLASH_MESURE_CALME = 0.20;   // « à chaque mesure, un flash du panneau à 20% »
const FLASH_MESURE_MS = 70;

// --- LES DEUX RÉGIMES ------------------------------------------------------
// Au-dessus de 8 temps : CALME. Le nombre est grand, le panneau ne s'assombrit
// pas, il n'y a ni anneaux ni grossissement. C'est le régime qui dure, donc
// celui qui doit se regarder sans fatigue - d'où une crête de luminance plus
// basse que celle du drame.
// De 8 à 1 : DRAMATIQUE. Tout ce que le brief décrit déjà.
// La bascule tombe sur le temps où le nombre passe à 8, et c'est une COUPURE :
// le chiffre saute au centre et le panneau s'assombrit d'un coup.
const REGIME_BASCULE = 8;
const CRETE_CALME = 0.70;
// L'INDICATEUR DE TEMPS : quatre points sous le nombre, un par temps de la
// mesure, celui du temps courant allumé. C'est l'indicateur des logiciels de
// musique, et il se lit sans y penser. Il ne vit qu'en régime calme : en
// régime dramatique le chiffre lui-même change à chaque temps, et un second
// marqueur de la même cadence n'ajoute rien à un panneau qu'on est en train
// d'éteindre.
const INDICATEUR_POINTS = 4;
const INDICATEUR_ECART = 3;        // cases entre deux points
const INDICATEUR_ALLUME = 1.0;
const INDICATEUR_ETEINT = 0.16;
const INDICATEUR_SOUS = 2;         // cases sous le nombre
// L'échelle maximale du nombre en régime calme. 3 remplit les trois quarts du
// panneau en hauteur ; la largeur la fait redescendre toute seule dès que le
// nombre a trois chiffres ou passe en mm:ss (voir _dessinerCalme).
const ECHELLE_CALME_MAX = 3;

// --- LA FIN DU MORCEAU -----------------------------------------------------
// Quand plus aucun drop ne reste, le nombre passe au décompte vers la FIN du
// morceau. Même régime, même lecture ; simplement, à zéro, il ne se passe rien
// de spectaculaire - le chiffre s'éteint. Ne pas faire ça laisserait
// l'afficheur bloqué sur un nombre qui ne bouge plus, ce qui est la seule
// chose qu'un décompte n'a pas le droit de faire.
const FIN_SANS_EXPLOSION = true;
// Au-delà de 999 temps, un nombre de temps ne se lit plus : à 128 BPM, 999
// temps font près de huit minutes. On passe en mm:ss, et on revient aux temps
// dès qu'on repasse dessous.
const SEUIL_MINUTAGE = 999;

// --- LE COMPTE À REBOURS ---------------------------------------------------
const REBOURS_TEMPS = 8;        // le compte à rebours complet
const REBOURS_COURT = 4;        // un second drop trop proche du premier
const REBOURS_RAPPROCHES = 16;  // « à moins de 16 temps l'un de l'autre »
// Le fond s'assombrit par soustraction : 8% à 8, noir absolu à 1.
const REBOURS_FOND_A_8 = 0.08;
const REBOURS_GROSSIT_DES = 4;  // à partir de 4, +8% par temps
const REBOURS_GROSSIT = 0.08;
const REBOURS_ANNEAUX_DES = 2;  // à partir de 2, une couronne d'anneaux
const ANNEAUX_N = 3;
const ANNEAU_LARGEUR = 1.4;     // en cases
const ANNEAU_VITESSE = 11;      // cases par temps

// --- L'ANTICIPATION LONGUE -------------------------------------------------
const ANTICIPATION_TEMPS = 32;

// --- LE DROP ---------------------------------------------------------------
const DROP_APLAT_TEMPS = 1;     // le panneau entier en aplat, un temps
const EXPLOSION_TEMPS = 2;      // les particules retombent en deux temps
// 400 emplacements de points, comme le brief le demande, mais lus comme
// 100 particules x 4 ÉCHANTILLONS DE TRAÎNÉE : un THREE.Points ne sait pas
// étirer un point, et la traînée demandée s'obtient en dessinant la même
// particule à quatre instants passés, de plus en plus faible. Analytique, sans
// état par image : la position à t - k.dt se calcule comme celle à t.
const EXPLOSION_PARTICULES = 100;
const EXPLOSION_TRAINEE = 4;
const EXPLOSION_POINTS = EXPLOSION_PARTICULES * EXPLOSION_TRAINEE;
const EXPLOSION_TRAINEE_DT = 0.035;   // en secondes
const EXPLOSION_VITESSE = [1.1, 3.0]; // unités de hauteur par seconde
const EXPLOSION_GRAVITE = 1.6;
// Part de radial dans la direction d'une particule ; le reste est tiré au
// hasard. Voir _armerExplosion.
const MELANGE_RADIAL = 0.45;

// --- LA COULEUR ------------------------------------------------------------
const REPLI = [0x00e5ff, 0xff2d55, 0xffd400];
const SAT = 0.85;
const LUM_POINT = 0.56;
// Le compte à rebours est TOUJOURS en blanc chaud, quelle que soit la palette :
// c'est le signal universel, et une palette ne doit pas pouvoir le déguiser.
const BLANC_CHAUD = [1.0, 0.955, 0.88];

// Le plancher de la zone de sécurité. 0.62 et non 0.32 : en compte à rebours le
// chiffre traverse la pochette, et un plancher bas y creuserait un trou sombre
// au milieu du seul objet que le spectateur regarde.
const PLANCHER_PARTAGE = 0.32;
const PLANCHER = 0.62;

const TIERS = {
  2: { anneaux: true, explosion: true },
  1: { anneaux: false, explosion: true },
  0: { anneaux: false, explosion: false }
};
const TIER_DEFAUT = 2;

function urlParam(nom) {
  if (typeof window === 'undefined' || !window.location) return null;
  return new URLSearchParams(window.location.search).get(nom);
}
function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }
function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

function versLineaire(c) { return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }

// THREE.ColorManagement est désactivé dans ce build : setHSL y écrit du sRGB
// brut, et une couleur posée sans conversion arrive 3,5 fois trop claire.
function poserHSL(couleur, h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (((h % 1) + 1) % 1) * 6;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0, g = 0, b = 0;
  if (hp < 1) { r = c; g = x; } else if (hp < 2) { r = x; g = c; }
  else if (hp < 3) { g = c; b = x; } else if (hp < 4) { g = x; b = c; }
  else if (hp < 5) { r = x; b = c; } else { r = c; b = x; }
  const m = l - c / 2;
  couleur.setRGB(versLineaire(r + m), versLineaire(g + m), versLineaire(b + m));
  return couleur;
}
function teinteDe(hex) {
  const r = ((hex >> 16) & 255) / 255, g = ((hex >> 8) & 255) / 255, b = (hex & 255) / 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
  if (d < 1e-6) return null;
  let h;
  if (mx === r) h = ((g - b) / d + (g < b ? 6 : 0)); else if (mx === g) h = (b - r) / d + 2; else h = (r - g) / d + 4;
  return h / 6;
}

const LUMA = 'vec3(0.2125, 0.7154, 0.0721)';

const ZONE_CHUNK = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;

  float zoneSecurite(vec2 s) {
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float a = mix(${PLANCHER_PARTAGE.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
    return ${PLANCHER.toFixed(2)} + (a - ${PLANCHER_PARTAGE.toFixed(2)})
         * ${((1 - PLANCHER) / (1 - PLANCHER_PARTAGE)).toFixed(6)};
  }
`;

// ---------------------------------------------------------------------------
// Le fond : du noir, et rien d'autre
// ---------------------------------------------------------------------------
const fondVertexShader = `
  precision mediump float;
  void main() { gl_Position = vec4(position.xy, 0.0, 1.0); }
`;
// Le dither est MULTIPLICATIF, comme dans poster.js : posé en lumière linéaire,
// un dither additif est multiplié par la pente de la courbe de sortie près de
// zéro - presque 13 - et le noir cesse d'être noir sur tout le cadre.
const fondFragmentShader = `
  precision mediump float;
  uniform vec2 uResolution;
  float hash21(vec2 p) {
    return fract(sin(mod(dot(p, vec2(12.9898, 78.233)), 6283.1853)) * 43758.5453);
  }
  void main() {
    gl_FragColor = vec4(vec3(0.0), 1.0);
  }
`;

// ---------------------------------------------------------------------------
// Les points : un InstancedMesh, une case par point
// ---------------------------------------------------------------------------
const pointVertexShader = `
  precision mediump float;

  attribute vec2 iCase;      // colonne, ligne
  attribute float iNiveau;   // 0..1, l'intensité voulue
  attribute float iTeinte;   // 0 = couleur de palette, 1 = blanc chaud

  // uAspect est déclaré par ZONE_CHUNK. Le redéclarer ici fait échouer la
  // compilation sur « redefinition » et le programme est abandonné : la couche
  // ne dessine rien, et les autres vont bien. C'est exactement la panne que
  // poster.js a déjà eue avec uResolution.
  uniform vec2 uCellules;    // colonnes, lignes
  uniform float uEcheleQuad; // le quad déborde la case pour laisser place au halo

  varying vec2 vLocal;
  varying float vNiveau;
  varying float vTeinte;
  varying float vAtt;

  ${ZONE_CHUNK}

  void main() {
    // Le pas d'une case, en unités de hauteur. Les deux valent la même chose
    // par construction (voir LIGNES/COLONNES) : les points sont carrés.
    float pasY = 2.0 / uCellules.y;
    float pasX = 2.0 * uAspect / uCellules.x;
    vec2 centre = vec2(
      (iCase.x + 0.5) * pasX - uAspect,
      1.0 - (iCase.y + 0.5) * pasY
    );
    vLocal = position.xy * (iNiveau > 0.01 ? uEcheleQuad : ${QUAD_ETEINT.toFixed(3)});
    vec2 monde = centre + vLocal * vec2(pasX, pasY) * 0.5;
    gl_Position = vec4(monde.x / uAspect, monde.y, 0.0, 1.0);
    vNiveau = iNiveau;
    vTeinte = iTeinte;
    vAtt = zoneSecurite(centre);
  }
`;

const pointFragmentShader = `
  precision mediump float;

  uniform vec3 uCouleur;
  uniform vec3 uBlanc;
  uniform float uEteint;     // le niveau des points ÉTEINTS, 0 = noir absolu
  uniform float uHaloGain;
  uniform float uCrete;      // la crête de luminance, qui suit le régime

  varying vec2 vLocal;
  varying float vNiveau;
  varying float vTeinte;
  varying float vAtt;

  void main() {
    float d = length(vLocal);
    // Le disque. Un demi-pixel d'adoucissement, pas un fondu : c'est de
    // l'antialiasing, et c'est la seule douceur que ce visualiseur s'autorise.
    float disque = 1.0 - smoothstep(${RAYON_POINT.toFixed(3)} - 0.06,
                                    ${RAYON_POINT.toFixed(3)} + 0.06, d);
    float h = max(0.0, 1.0 - d / ${RAYON_HALO.toFixed(3)});
    float halo = h * h * h;

    vec3 teinte = mix(uCouleur, uBlanc, vTeinte);
    // LE POINT ÉTEINT est de la même couleur, très sombre : un panneau de LED
    // hors tension garde sa matière. Il ne prend PAS de halo - un point éteint
    // n'éclaire rien.
    vec3 col = teinte * (disque * uEteint);
    col += teinte * vNiveau * (disque + halo * ${'${HALO}'});

    col *= vAtt;
    // EN LUMINANCE, parce que c'est la luminance que la passe de bloom seuille.
    // Plafonner au canal maximal laisserait une teinte saturée très loin sous
    // le seuil, et écraserait un blanc chaud jusqu'à l'y faire passer.
    float lum = dot(col, ${LUMA});
    if (lum > uCrete) col *= uCrete / lum;
    if (max(col.r, max(col.g, col.b)) < 0.0015) discard;
    gl_FragColor = vec4(col, 1.0);
  }
`;

// ---------------------------------------------------------------------------
// L'explosion : un THREE.Points, positions analytiques
// ---------------------------------------------------------------------------
const explosionVertexShader = `
  precision mediump float;

  attribute vec2 aOrigine;   // d'où part la particule, unités de hauteur
  attribute vec2 aDir;       // direction radiale, normalisée
  attribute float aVitesse;
  attribute float aRang;     // 0..3 : l'échantillon de traînée

  // uAspect : voir le vertex des points, même raison.
  uniform float uT;          // secondes depuis le drop
  uniform float uVie;        // durée totale, secondes
  uniform float uTaillePx;

  varying float vAlpha;

  ${ZONE_CHUNK}

  void main() {
    // La traînée : la MÊME particule, quatre instants en arrière. Rien à
    // mémoriser d'une image à l'autre, la position à t - k.dt se calcule
    // exactement comme celle à t.
    float t = uT - aRang * ${EXPLOSION_TRAINEE_DT.toFixed(4)};
    if (t < 0.0 || t > uVie) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); vAlpha = 0.0; return; }
    vec2 p = aOrigine + aDir * (aVitesse * t);
    p.y -= ${EXPLOSION_GRAVITE.toFixed(3)} * t * t;
    float k = 1.0 - t / uVie;
    // LINÉAIRE, pas au carré. L'aplat du drop occupe le premier des deux temps ;
    // au carré, les particules étaient déjà tombées à 25% quand il se retire, et
    // l'explosion - le geste que ce visualiseur existe pour faire - ne se voyait
    // sur aucune image. La traînée s'efface plus vite que la tête, sinon c'est
    // une comète.
    vAlpha = k * (1.0 - aRang * 0.22) * zoneSecurite(p);
    gl_Position = vec4(p.x / uAspect, p.y, 0.0, 1.0);
    gl_PointSize = uTaillePx * (0.45 + 0.55 * k) * (1.0 - aRang * 0.15);
  }
`;

const explosionFragmentShader = `
  precision mediump float;
  // uBlanc, PAS uCouleur : ce qui explose est le chiffre, et le chiffre est en
  // blanc chaud. Des particules dans la teinte de palette se perdaient en plus
  // dans l'aplat du drop, qui est de cette teinte exactement.
  uniform vec3 uBlanc;
  varying float vAlpha;
  void main() {
    vec2 uv = gl_PointCoord - vec2(0.5);
    float d = length(uv) * 2.0;
    if (d > 1.0) discard;
    float bord = (1.0 - d) * (1.0 - d);
    vec3 col = uBlanc * vAlpha * bord;
    float lum = dot(col, ${LUMA});
    if (lum > ${CRETE_CHIFFRE.toFixed(2)}) col *= ${CRETE_CHIFFRE.toFixed(2)} / lum;
    gl_FragColor = vec4(col, 1.0);
  }
`;

export class CountdownVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : TIER_DEFAUT;
    this._tier = TIERS[tier] ? tier : TIER_DEFAUT;
    this._opts = TIERS[this._tier];

    // ?countdown=8 force un compte à rebours de N temps à partir de maintenant.
    const forceN = parseInt(urlParam('countdown') || '', 10);
    this._reboursForce = isFinite(forceN) && forceN > 0 ? Math.min(forceN, 16) : null;
    // ?countdownls=1 affiche le temps restant et les raisons d'éligibilité.
    this._trace = urlParam('countdownls') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    scene.fog = null;

    this._colonnes = LIGNES;   // recalculé à la première image, dès l'aspect connu
    this._uniforms = {
      uAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
      uSafeFeather: safeZoneUniforms.uSafeFeather,
      uResolution: { value: new THREE.Vector2(1280, 720) },
      uCellules: { value: new THREE.Vector2(96, LIGNES) },
      uEcheleQuad: { value: RAYON_HALO * 2 },
      uCouleur: { value: new THREE.Color() },
      uBlanc: { value: new THREE.Color(versLineaire(BLANC_CHAUD[0]), versLineaire(BLANC_CHAUD[1]), versLineaire(BLANC_CHAUD[2])) },
      uEteint: { value: ETEINT },
      uCrete: { value: CRETE_CALME },
      uHaloGain: { value: HALO_GAIN_MIN },
      // l'explosion
      uT: { value: 0 },
      uVie: { value: 1 },
      uTaillePx: { value: 6 }
    };

    this._construireFond();
    this._construirePoints();
    if (this._opts.explosion) this._construireExplosion();

    this._temps = 0;
    this._grille = null;
    this._dernierBeatIndex = null;
    this._claqueA = -9;          // naissance du claquement du chiffre de temps
    this._claqueMesure = -9;
    this._flashMesure = -9;
    this._tempsDansMesure = 1;
    this._nombre = null;
    this._versLaFin = false;
    this._rebours = 0;           // 0 = pas de compte à rebours, sinon 8..1
    this._reboursDe = 0;         // de combien il est parti (8 ou 4)
    this._reboursDebutMs = null;
    this._dropAtMs = null;
    this._dropVuAtMs = null;
    this._explosionT = -1;
    this._aplatT = -1;
    this._anticipation = 0;
    this._basse = 0.4;
    this._aigu = 0.4;
    this._paletteVue = undefined;
    this._teintes = [];
    this._phraseVue = null;
    this._teinteIdx = 0;
    this._appliquerPalette(null);
    this._graine = 7;
    this._forceDebutMs = null;
    this._dernierTrace = 0;
  }

  _hasard() {
    this._graine = (this._graine * 1103515245 + 12345) % 2147483648;
    return this._graine / 2147483648;
  }

  _construireFond() {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
      -1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0]), 3));
    g.setIndex([0, 1, 2, 0, 2, 3]);
    this._fondGeo = g;
    this._fondMat = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: fondVertexShader,
      fragmentShader: fondFragmentShader,
      depthTest: false, depthWrite: false, transparent: false
    });
    this._fond = new THREE.Mesh(g, this._fondMat);
    this._fond.frustumCulled = false;
    this._fond.renderOrder = 0;
    this._scene.add(this._fond);
  }

  _construirePoints() {
    const g = new THREE.InstancedBufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
      -1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0]), 3));
    g.setIndex([0, 1, 2, 0, 2, 3]);
    this._att = {
      iCase: new Float32Array(MAX_POINTS * 2),
      iNiveau: new Float32Array(MAX_POINTS),
      iTeinte: new Float32Array(MAX_POINTS)
    };
    g.setAttribute('iCase', new THREE.InstancedBufferAttribute(this._att.iCase, 2));
    g.setAttribute('iNiveau', new THREE.InstancedBufferAttribute(this._att.iNiveau, 1));
    g.setAttribute('iTeinte', new THREE.InstancedBufferAttribute(this._att.iTeinte, 1));
    g.instanceCount = 0;
    this._pointGeo = g;
    this._pointMat = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: pointVertexShader,
      fragmentShader: pointFragmentShader.replace('${HALO}', 'uHaloGain'),
      transparent: true, depthTest: false, depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    this._points = new THREE.Mesh(g, this._pointMat);
    this._points.frustumCulled = false;
    this._points.renderOrder = 1;
    this._scene.add(this._points);
  }

  _construireExplosion() {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(EXPLOSION_POINTS * 3), 3));
    this._exp = {
      origine: new Float32Array(EXPLOSION_POINTS * 2),
      dir: new Float32Array(EXPLOSION_POINTS * 2),
      vitesse: new Float32Array(EXPLOSION_POINTS),
      rang: new Float32Array(EXPLOSION_POINTS)
    };
    for (let i = 0; i < EXPLOSION_POINTS; i++) this._exp.rang[i] = i % EXPLOSION_TRAINEE;
    g.setAttribute('aOrigine', new THREE.BufferAttribute(this._exp.origine, 2));
    g.setAttribute('aDir', new THREE.BufferAttribute(this._exp.dir, 2));
    g.setAttribute('aVitesse', new THREE.BufferAttribute(this._exp.vitesse, 1));
    g.setAttribute('aRang', new THREE.BufferAttribute(this._exp.rang, 1));
    this._expGeo = g;
    this._expMat = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: explosionVertexShader,
      fragmentShader: explosionFragmentShader,
      transparent: true, depthTest: false, depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    this._explosion = new THREE.Points(g, this._expMat);
    this._explosion.frustumCulled = false;
    this._explosion.renderOrder = 2;
    this._explosion.visible = false;
    this._scene.add(this._explosion);
  }

  // --- la couleur -----------------------------------------------------------
  _appliquerPalette(palette) {
    const vifs = (palette && palette.vividColors && palette.vividColors.length >= 1)
      ? palette.vividColors : REPLI;
    const t = [];
    for (const v of vifs) { const h = teinteDe(v); if (h != null) t.push(h); }
    if (!t.length) t.push(0.52);
    this._teintes = t;
    this._teinteIdx = 0;
    poserHSL(this._uniforms.uCouleur.value, t[0], SAT, LUM_POINT);
  }

  // --- la grille de cases ---------------------------------------------------
  // Recalculée quand l'aspect change, et seulement là : le nombre de colonnes
  // ne dépend que de lui.
  _majGrille(asp) {
    const cols = clamp(Math.round(LIGNES * asp), COLONNES_MIN, COLONNES_MAX);
    if (cols === this._colonnes) return;
    this._colonnes = cols;
    const a = this._att;
    for (let l = 0; l < LIGNES; l++) {
      for (let c = 0; c < cols; c++) {
        const i = l * cols + c;
        a.iCase[i * 2] = c; a.iCase[i * 2 + 1] = l;
      }
    }
    this._pointGeo.instanceCount = cols * LIGNES;
    this._pointGeo.getAttribute('iCase').needsUpdate = true;
    this._uniforms.uCellules.value.set(cols, LIGNES);
  }

  // --- dessiner dans la matrice ---------------------------------------------
  _effacer() { this._att.iNiveau.fill(0); this._att.iTeinte.fill(0); }

  _poserCase(c, l, niveau, blanc) {
    if (c < 0 || l < 0 || c >= this._colonnes || l >= LIGNES) return;
    const i = l * this._colonnes + c;
    if (niveau > this._att.iNiveau[i]) {
      this._att.iNiveau[i] = niveau;
      this._att.iTeinte[i] = blanc ? 1 : 0;
    }
  }

  // La largeur d'un texte, en cases, à une échelle donnée.
  _largeurTexte(texte, echelle) {
    let w = 0;
    for (let i = 0; i < texte.length; i++) {
      if (i) w += GLYPHE_ECART;
      w += glyphe(texte[i]).large;
    }
    return w * echelle;
  }

  // Un TEXTE - un nombre à un ou plusieurs chiffres, ou un mm:ss - coin
  // haut-gauche en (c0, l0). Rend sa largeur en cases.
  _poserTexte(texte, c0, l0, echelle, niveau, blanc) {
    let c = c0;
    for (let i = 0; i < texte.length; i++) {
      const g = glyphe(texte[i]);
      for (let y = 0; y < GLYPHE_H; y++) {
        const bits = g.bits[y];
        for (let x = 0; x < g.large; x++) {
          if (!(bits & (1 << (g.large - 1 - x)))) continue;
          for (let dy = 0; dy < echelle; dy++) {
            for (let dx = 0; dx < echelle; dx++) {
              this._poserCase(c + x * echelle + dx, l0 + y * echelle + dy, niveau, blanc);
            }
          }
        }
      }
      c += (g.large + GLYPHE_ECART) * echelle;
    }
    return c - c0 - GLYPHE_ECART * echelle;
  }

  // Les cases allumées d'un texte, pour que l'explosion parte d'elles.
  _casesDuTexte(texte, c0, l0, echelle) {
    const out = [];
    let c = c0;
    for (let i = 0; i < texte.length; i++) {
      const g = glyphe(texte[i]);
      for (let y = 0; y < GLYPHE_H; y++) {
        const bits = g.bits[y];
        for (let x = 0; x < g.large; x++) {
          if (!(bits & (1 << (g.large - 1 - x)))) continue;
          out.push([c + x * echelle + echelle * 0.5, l0 + y * echelle + echelle * 0.5]);
        }
      }
      c += (g.large + GLYPHE_ECART) * echelle;
    }
    return out;
  }

  // Le centre d'une case, en unités de hauteur - la même formule que le vertex.
  _casePos(c, l, asp) {
    const pasY = 2 / LIGNES, pasX = 2 * asp / this._colonnes;
    return [(c + 0.5) * pasX - asp, 1 - (l + 0.5) * pasY];
  }

  _armerExplosion(cases, asp) {
    if (!this._opts.explosion || !cases.length) return;
    const e = this._exp;
    // Le centre du chiffre : l'explosion est RADIALE depuis lui.
    let cx = 0, cy = 0;
    for (const k of cases) { cx += k[0]; cy += k[1]; }
    cx /= cases.length; cy /= cases.length;
    const centre = this._casePos(cx - 0.5, cy - 0.5, asp);
    for (let p = 0; p < EXPLOSION_PARTICULES; p++) {
      const k = cases[Math.floor(this._hasard() * cases.length)];
      const pos = this._casePos(k[0] - 0.5, k[1] - 0.5, asp);
      let dx = pos[0] - centre[0], dy = pos[1] - centre[1];
      const len = Math.hypot(dx, dy);
      if (len < 1e-4) { const a = this._hasard() * Math.PI * 2; dx = Math.cos(a); dy = Math.sin(a); }
      else { dx /= len; dy /= len; }
      // RADIAL, MAIS PAS SEULEMENT. Un chiffre n'est pas un disque : « 1 » est
      // une barre verticale, et la direction radiale depuis son centre y est
      // presque partout verticale. Mesuré, ça donnait un jet vers le haut et le
      // bas, pas un éclatement - toutes les particules dans deux octants sur
      // huit. On mélange donc le radial avec une direction tirée au hasard : un
      // glyphe large garde son biais radial, un glyphe étroit éclate quand même.
      const a = this._hasard() * Math.PI * 2;
      let ndx = dx * MELANGE_RADIAL + Math.cos(a) * (1 - MELANGE_RADIAL);
      let ndy = dy * MELANGE_RADIAL + Math.sin(a) * (1 - MELANGE_RADIAL);
      const nl = Math.hypot(ndx, ndy) || 1;
      ndx /= nl; ndy /= nl;
      const v = EXPLOSION_VITESSE[0] + this._hasard() * (EXPLOSION_VITESSE[1] - EXPLOSION_VITESSE[0]);
      for (let r = 0; r < EXPLOSION_TRAINEE; r++) {
        const i = p * EXPLOSION_TRAINEE + r;
        e.origine[i * 2] = pos[0]; e.origine[i * 2 + 1] = pos[1];
        e.dir[i * 2] = ndx; e.dir[i * 2 + 1] = ndy;
        e.vitesse[i] = v;
      }
    }
    this._expGeo.getAttribute('aOrigine').needsUpdate = true;
    this._expGeo.getAttribute('aDir').needsUpdate = true;
    this._expGeo.getAttribute('aVitesse').needsUpdate = true;
  }

  _periode() { return this._bpm > 0 ? 60 / this._bpm : 0.5; }

  // COMBIEN DE TEMPS AVANT LA FIN DU MORCEAU. Avec la timeline, c'est une
  // différence d'indices de temps, exacte comme le reste ; sans elle, la durée
  // et le bpm suffisent - et c'est tout ce dont ce calcul a besoin, contrairement
  // au décompte vers un drop.
  _tempsAvantLaFin(timeline, profile, beatIndex) {
    const finMs = profile.duration;
    if (!finMs || finMs <= 0) return null;
    const nowMs = (profile.estimatedPosition != null ? profile.estimatedPosition : profile.position) * 1000;
    if (timeline && beatIndex != null) {
      const g = timeline.lookupBeatPhase(finMs);
      return Math.max(0, Math.round(g.beatIndex + g.beatPhase) - Math.floor(beatIndex));
    }
    const per = this._periode();
    return per > 0 ? Math.max(0, Math.floor((finMs - nowMs) / 1000 / per)) : null;
  }

  // LE NOMBRE, EN TEXTE. Au-delà de 999 temps un compte de temps ne se lit
  // plus - à 128 BPM, 999 temps font presque huit minutes - alors on passe en
  // mm:ss, et on revient aux temps dès qu'on repasse dessous. Le nombre garde
  // la même signification dans les deux cas : le temps qui reste.
  _texteDuNombre(n) {
    if (n > SEUIL_MINUTAGE) {
      const s = Math.round(n * this._periode());
      const m = Math.floor(s / 60);
      const r = s % 60;
      return m + ':' + (r < 10 ? '0' : '') + r;
    }
    return String(Math.max(0, n | 0));
  }

  update(deltaTime, profile) {
    // PAUSE : gel complet. Le décompte se fige sur son chiffre - il ne
    // continue pas dans le vide, et il ne s'efface pas non plus.
    if (profile.playbackState !== 'PLAYING') return;

    this._temps += deltaTime;
    const u = this._uniforms;
    const director = profile.director;
    const asp = safeZoneUniforms.uAspect.value || 1.78;
    this._majGrille(asp);
    this._bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;

    if (typeof window !== 'undefined') {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      u.uResolution.value.set(window.innerWidth * dpr, window.innerHeight * dpr);
      // La taille d'un point de l'afficheur, pas un grain : une particule est un
      // point LED qui s'est détaché, elle doit en avoir le calibre.
      u.uTaillePx.value = Math.max(4, (window.innerHeight * dpr) / LIGNES * 0.60);
    }

    const bandes = (director && director.hasReliableGrid && director.bands) ? director.bands : null;
    if (bandes) {
      this._basse += ((bandes.low != null ? bandes.low : 0.4) - this._basse) * Math.min(1, deltaTime / 0.18);
      this._aigu += ((bandes.high != null ? bandes.high : 0.4) - this._aigu) * Math.min(1, deltaTime / 0.30);
    }
    u.uHaloGain.value = HALO_GAIN_MIN + (HALO_GAIN_MAX - HALO_GAIN_MIN) * clamp01(this._basse);

    if (profile.artworkPalette !== this._paletteVue) {
      this._paletteVue = profile.artworkPalette;
      this._appliquerPalette(profile.artworkPalette);
    }

    // --- LA GRILLE EXACTE ---------------------------------------------------
    // lookupBeatPhase rend l'indice de temps RÉEL, lu dans beat_times_ms. Sans
    // timeline on retombe sur la phase du profil : le mode normal compte quand
    // même, le compte à rebours ne s'arme jamais (voir plus bas).
    const timeline = profile.analysisTimeline || null;
    const nowMs = (profile.estimatedPosition != null ? profile.estimatedPosition : profile.position) * 1000;
    let beatIndex = null, beatPhase = null, barIndex = null, phraseIndex = null, sectionIndex = null;
    if (timeline) {
      const g = timeline.lookupBeatPhase(nowMs);
      beatIndex = g.beatIndex; beatPhase = g.beatPhase;
      barIndex = g.barIndex; phraseIndex = g.phraseIndex; sectionIndex = g.sectionIndex;
    } else if (profile.beatPhase != null) {
      beatPhase = profile.beatPhase;
      this._beatLibre = (this._beatLibre || 0);
      beatIndex = this._beatLibre;
    }

    // Front montant de temps.
    let nouveauTemps = false;
    if (timeline) {
      if (this._dernierBeatIndex != null && beatIndex > this._dernierBeatIndex) nouveauTemps = true;
      this._dernierBeatIndex = beatIndex;
    } else if (beatPhase != null) {
      if (this._beatPhasePrec != null && beatPhase < this._beatPhasePrec) {
        nouveauTemps = true; this._beatLibre = (this._beatLibre || 0) + 1;
        beatIndex = this._beatLibre;
        this._dernierBeatIndex = beatIndex;
      }
      this._beatPhasePrec = beatPhase;
    }

    if (nouveauTemps) {
      this._claqueA = this._temps;
      const b = beatIndex || 0;
      // La seule chose qu'on garde de la structure musicale : POSITION DANS LA
      // MESURE, et uniquement pour allumer le bon point de l'indicateur. Aucun
      // nombre à l'écran ne demande plus de savoir ce qu'est une mesure.
      this._tempsDansMesure = (((b % 4) + 4) % 4) + 1;
      if (this._tempsDansMesure === 1) this._flashMesure = this._temps;
    }
    if (timeline) {
      // La couleur change sur la PHRASE, en tirant dans la palette.
      if (this._phraseVue !== phraseIndex) {
        this._phraseVue = phraseIndex;
        if (this._teintes.length > 1) {
          this._teinteIdx = (this._teinteIdx + 1) % this._teintes.length;
        }
        poserHSL(u.uCouleur.value, this._teintes[this._teinteIdx], SAT, LUM_POINT);
      }
    }

    // --- LE PROCHAIN DROP, ET LE COMPTE À REBOURS ---------------------------
    // Tout ce bloc est gardé par `timeline` : SANS ANALYSE, AUCUN COMPTE À
    // REBOURS. C'est la règle du brief, et elle tient ici par construction
    // plutôt que par un test de plus.
    let reboursN = 0, dropDansMs = null;
    if (timeline) {
      const info = dropsAhead(timeline, nowMs);
      dropDansMs = info.nextMsUntil;
      if (info.nextAtMs != null) {
        // LE TEMPS LE PLUS PROCHE, pas le temps précédent. Un drop tombe
        // rarement pile sur un temps : lookupBeatPhase rend l'indice PLANCHER,
        // et s'en servir faisait finir le décompte AVANT le drop. Mesuré sur un
        // drop posé au milieu d'un intervalle : le « 1 » s'effaçait un demi-
        // temps trop tôt, et l'afficheur repassait en mode normal juste avant
        // l'explosion. Arrondi au plus proche, le « 1 » tient jusqu'au drop.
        const g = timeline.lookupBeatPhase(info.nextAtMs);
        const dropBeat = Math.round(g.beatIndex + g.beatPhase);
        // JAMAIS ZÉRO AVANT LE DROP. L'arrondi ci-dessus rapproche le décompte
        // du drop, il ne le colle pas dessus : un drop posé à 0.4997 temps d'un
        // temps s'arrondit vers le BAS, et le décompte atteignait alors 0 un
        // demi-temps avant l'explosion - l'afficheur repassait en mode normal
        // juste avant le drop, ce qui est le seul moment où il ne doit surtout
        // pas le faire. Le « 1 » tient donc jusqu'à l'instant exact, et c'est
        // dropsAhead qui le retire en retirant le drop lui-même.
        const restants = Math.max(1, dropBeat - Math.floor(beatIndex));
        // DEUX DROPS RAPPROCHÉS : seul le premier a droit au compte à rebours
        // complet ; le second en a un court, sur quatre temps.
        const precedent = this._dropPrecedentBeat;
        const depart = (precedent != null && dropBeat - precedent < REBOURS_RAPPROCHES)
          ? REBOURS_COURT : REBOURS_TEMPS;
        if (restants >= 1 && restants <= depart) { reboursN = restants; this._reboursDe = depart; }
        // LE NOMBRE, qui est le sujet : combien de temps avant le prochain drop.
        // Il est affiché en permanence, pas seulement dans les huit derniers.
        this._nombre = restants;
        this._versLaFin = false;
        this._dropAtMs = info.nextAtMs;
      } else {
        // PLUS AUCUN DROP DEVANT : le nombre passe à la fin du morceau. Un
        // décompte qui se fige sur un nombre immobile est la seule chose qu'un
        // décompte n'a pas le droit de faire.
        this._dropAtMs = null;
        this._versLaFin = true;
        this._nombre = this._tempsAvantLaFin(timeline, profile, beatIndex);
        if (this._nombre != null && this._nombre >= 1 && this._nombre <= REBOURS_TEMPS) {
          reboursN = this._nombre; this._reboursDe = REBOURS_TEMPS;
        }
      }
      // Le drop est-il passé depuis la dernière image ?
      if (this._dropAttenduMs != null && nowMs >= this._dropAttenduMs && this._dropVuAtMs !== this._dropAttenduMs) {
        this._dropVuAtMs = this._dropAttenduMs;
        const gp = timeline.lookupBeatPhase(this._dropAttenduMs);
        this._dropPrecedentBeat = Math.round(gp.beatIndex + gp.beatPhase);
        this._declencherDrop(profile, asp);
      }
      this._dropAttenduMs = info.nextAtMs;
    }

    // ?countdown=N : un compte à rebours forcé, pour voir le geste sans
    // attendre un vrai drop. Il ne DÉCLENCHE rien - pas d'explosion sur un drop
    // qui n'existe pas - il ne fait que montrer les chiffres.
    if (this._reboursForce) {
      if (this._forceDebutMs == null) this._forceDebutMs = beatIndex;
      const ecoule = (beatIndex || 0) - this._forceDebutMs;
      const n = this._reboursForce - ecoule;
      reboursN = n >= 1 ? n : 0;
      this._reboursDe = this._reboursForce;
      if (n < 0) this._forceDebutMs = beatIndex;   // en boucle
      this._nombre = Math.max(0, n);
      this._versLaFin = false;
    }
    this._rebours = reboursN;
    // SANS ANALYSE il n'y a pas de nombre à montrer : Countdown n'est jamais
    // sélectionné dans ce cas, mais la console VDJ peut l'imposer et la vignette
    // l'instancie directement. On affiche alors le décompte vers la fin du
    // morceau, qui ne demande que la durée - jamais un drop, qu'on ne connaît
    // pas. Rien n'est inventé.
    if (!timeline && !this._reboursForce) {
      this._versLaFin = true;
      this._nombre = this._tempsAvantLaFin(null, profile, beatIndex);
    }

    // --- LE RENDU -----------------------------------------------------------
    this._effacer();
    // LE FOND DU PANNEAU. En régime dramatique il s'assombrit par soustraction :
    // 8% à 8, noir absolu à 1. La tension monte parce qu'on RETIRE. En régime
    // calme il ne s'assombrit pas du tout - c'est ce qui sépare les deux.
    if (reboursN > 0) {
      const k = (reboursN - 1) / Math.max(1, this._reboursDe - 1);
      u.uEteint.value = REBOURS_FOND_A_8 * k;
    } else {
      u.uEteint.value = ETEINT;
    }
    // LA CRÊTE DE LUMINANCE suit le régime : 0.92 dans le drame, 0.70 dans le
    // calme. Le calme dure la majorité du morceau, il doit se regarder sans
    // fatigue ; le drame dure huit temps et a le droit de déborder.
    u.uCrete.value = reboursN > 0 ? CRETE_CHIFFRE : CRETE_CALME;

    const claqueMs = (this._temps - this._claqueA) * 1000;
    // LE CLAQUEMENT : noir franc 40 ms, puis rallumage à 130% qui revient à
    // 100% en 90 ms. Linéaire, sans rebond, et surtout sans fondu.
    let claque;
    if (claqueMs < CLAQUE_NOIR_MS) claque = 0;
    else if (claqueMs < CLAQUE_NOIR_MS + CLAQUE_PUNCH_MS) {
      const t = (claqueMs - CLAQUE_NOIR_MS) / CLAQUE_PUNCH_MS;
      claque = CLAQUE_PUNCH + (1 - CLAQUE_PUNCH) * t;
    } else claque = 1;

    if (reboursN > 0) {
      this._dessinerRebours(reboursN, claque, asp);
    } else if (this._nombre != null) {
      this._dessinerCalme(claque, this._texteDuNombre(this._nombre));
    }

    // L'aplat du drop : le panneau entier dans la couleur de palette, un temps.
    if (this._aplatT >= 0) {
      this._aplatT += deltaTime;
      const vie = DROP_APLAT_TEMPS * this._periode();
      if (this._aplatT >= vie) this._aplatT = -1;
      else {
        // Pas de fondu : plein jusqu'au bout, puis rien.
        const n = this._att.iNiveau, t = this._att.iTeinte;
        const total = this._colonnes * LIGNES;
        for (let i = 0; i < total; i++) { n[i] = 1; t[i] = 0; }
      }
    }

    // Le flash de mesure : le panneau entier, bref, à 30%.
    const flashMs = (this._temps - this._flashMesure) * 1000;
    if (flashMs >= 0 && flashMs < FLASH_MESURE_MS && this._aplatT < 0) {
      const niv = reboursN > 0 ? FLASH_MESURE_DRAMATIQUE : FLASH_MESURE_CALME;
      const n = this._att.iNiveau;
      const total = this._colonnes * LIGNES;
      for (let i = 0; i < total; i++) if (n[i] < niv) n[i] = niv;
    }

    this._pointGeo.getAttribute('iNiveau').needsUpdate = true;
    this._pointGeo.getAttribute('iTeinte').needsUpdate = true;

    // L'explosion.
    if (this._explosion) {
      if (this._explosionT >= 0) {
        this._explosionT += deltaTime;
        const vie = EXPLOSION_TEMPS * this._periode();
        u.uT.value = this._explosionT;
        u.uVie.value = vie;
        this._explosion.visible = true;
        if (this._explosionT > vie + EXPLOSION_TRAINEE * EXPLOSION_TRAINEE_DT) {
          this._explosionT = -1; this._explosion.visible = false;
        }
      } else this._explosion.visible = false;
    }

    if (this._trace && this._temps - this._dernierTrace > 0.5) {
      this._dernierTrace = this._temps;
      // eslint-disable-next-line no-console
      console.log('[countdown] analyse=' + (!!timeline)
        + ' grille=' + (director && director.hasReliableGrid)
        + ' drop dans=' + (dropDansMs != null ? Math.round(dropDansMs) + ' ms' : 'aucun')
        + ' rebours=' + (reboursN || '-')
        + ' anticipation=' + this._anticipation.toFixed(2)
        + ' seuil de drop=' + DROP_SCORE_MIN);
    }
  }

  // LA FIN DU MORCEAU N'EXPLOSE PAS. Le décompte vers la fin se lit exactement
  // comme celui vers un drop - c'est tout l'intérêt de n'avoir qu'un nombre -
  // mais à zéro il ne se passe rien : le chiffre s'éteint. Promettre une
  // explosion là où il n'y a que la fin du morceau serait le même mensonge que
  // compter à rebours vers un drop inconnu.
  _declencherDrop(profile, asp) {
    if (this._versLaFin && FIN_SANS_EXPLOSION) return;
    // Flash blanc plein cadre, deux images, par le canal partagé de la chaîne.
    if (profile.director) profile.director.flash = 1;
    this._flashRestant = 2;
    this._aplatT = 0;
    // Le chiffre EXPLOSE : les particules partent des cases qu'il occupait.
    if (this._opts.explosion) {
      // À LA TAILLE QU'IL AVAIT. Sur le dernier temps le chiffre est à son
      // maximum ; faire partir les particules de sa taille de DÉPART les ferait
      // naître à l'intérieur de lui-même.
      const ech = this._echelleReboursMax();
      const large = GLYPHE_L * ech, haut = GLYPHE_H * ech;
      const c0 = Math.round((this._colonnes - large) / 2);
      const l0 = Math.round((LIGNES - haut) / 2);
      this._armerExplosion(this._casesDuTexte('1', c0, l0, ech), asp);
      this._explosionT = 0;
    }
  }

  // L'échelle du chiffre de compte à rebours. PAS la plus grande qui tienne :
  // celle-là saturait déjà la hauteur du panneau, et le grossissement demandé à
  // partir de 4 était alors écrêté à sa propre valeur de départ - le chiffre ne
  // bougeait pas d'un pixel entre 8 et 1. Il part donc à trois quarts de
  // hauteur et finit plein cadre.
  _echelleRebours() { return Math.max(1, Math.floor(LIGNES / GLYPHE_H) - 1); }
  _echelleReboursMax() { return Math.max(1, Math.floor(LIGNES / GLYPHE_H)); }

  _dessinerRebours(n, claque, asp) {
    // À partir de 4, le chiffre grossit de 8% par temps. L'échelle de la
    // matrice est ENTIÈRE - on ne peut pas allumer un tiers de LED - donc le
    // grossissement se lit sur l'échelle arrondie, et le chiffre saute d'un
    // cran plutôt que de grandir continûment. C'est aussi ce que ferait un vrai
    // afficheur, et c'est cohérent avec « aucun fondu ».
    let ech = this._echelleRebours();
    if (n <= REBOURS_GROSSIT_DES) {
      const crans = REBOURS_GROSSIT_DES - n + 1;
      const f = Math.pow(1 + REBOURS_GROSSIT, crans);
      ech = Math.max(1, Math.min(this._echelleReboursMax(), Math.round(ech * f)));
    }
    const large = GLYPHE_L * ech, haut = GLYPHE_H * ech;
    const c0 = Math.round((this._colonnes - large) / 2);
    const l0 = Math.round((LIGNES - haut) / 2);
    // TOUJOURS EN BLANC CHAUD, quelle que soit la palette.
    this._poserTexte(String(n), c0, l0, ech, claque, true);

    // À partir de 2, une couronne d'anneaux d'écho part du chiffre.
    if (this._opts.anneaux && n <= REBOURS_ANNEAUX_DES) {
      const depuis = (this._temps - this._claqueA) / Math.max(0.05, this._periode());
      const cc = this._colonnes * 0.5, cl = LIGNES * 0.5;
      for (let k = 0; k < ANNEAUX_N; k++) {
        const r = (depuis - k * 0.16) * ANNEAU_VITESSE;
        if (r <= 0) continue;
        const niv = Math.max(0, 1 - depuis) * (1 - k * 0.25) * 0.8;
        if (niv <= 0.01) continue;
        this._anneau(cc, cl, r, niv);
      }
    }
  }

  // Un anneau de cases, en coordonnées de matrice. Les cases étant carrées, un
  // cercle en cases EST un cercle à l'écran.
  _anneau(cc, cl, r, niveau) {
    const rmax = Math.ceil(r + ANNEAU_LARGEUR);
    for (let l = Math.max(0, Math.floor(cl - rmax)); l <= Math.min(LIGNES - 1, Math.ceil(cl + rmax)); l++) {
      for (let c = Math.max(0, Math.floor(cc - rmax)); c <= Math.min(this._colonnes - 1, Math.ceil(cc + rmax)); c++) {
        const d = Math.hypot(c + 0.5 - cc, l + 0.5 - cl);
        if (Math.abs(d - r) > ANNEAU_LARGEUR * 0.5) continue;
        this._poserCase(c, l, niveau, true);
      }
    }
  }

  // LE RÉGIME CALME. Un seul nombre, grand, centré, et rien d'autre - sauf
  // l'indicateur de temps, quatre points sous lui.
  //
  // OÙ LE POSER. « Au-dessus de la pochette » n'est pas toujours possible : la
  // zone de sécurité mesurée laisse une dizaine de cases au-dessus d'elle sur
  // un cadre 16:9, et un glyphe en fait huit à l'échelle 1 - le nombre y serait
  // minuscule, ce qui rate le seul critère de ce visualiseur. On prend donc la
  // plus grande échelle qui tienne en LARGEUR, on pose le bloc aussi haut que
  // possible, et on le laisse mordre sur la pochette si la hauteur libre ne
  // suffit pas : le plancher de zone est à 0.62, le nombre y reste lisible.
  _dessinerCalme(claque, texte) {
    // R19 : la place se calcule depuis le bloc pochette au centre de scène
    const sc = safeZoneUniforms.uStageCenter.value;
    const sh = safeZoneUniforms.uStageHalfSize.value;
    // La ligne de la matrice où commence la pochette.
    const ligneHautPochette = Math.floor((1 - (sc.y + sh.y)) / (2 / LIGNES));

    let ech = ECHELLE_CALME_MAX;
    while (ech > 1 && this._largeurTexte(texte, ech) > this._colonnes - 4) ech--;
    const haut = GLYPHE_H * ech;
    const large = this._largeurTexte(texte, ech);
    const c0 = Math.round((this._colonnes - large) / 2);
    // Le bloc entier - nombre plus indicateur - veut tenir au-dessus de la
    // pochette ; sinon il part du haut du panneau et déborde.
    const blocHaut = haut + INDICATEUR_SOUS + 1;
    const l0 = blocHaut <= ligneHautPochette
      ? Math.max(1, Math.round((ligneHautPochette - blocHaut) / 2) + 1)
      : 1;

    this._poserTexte(texte, c0, l0, ech, claque, false);

    // L'INDICATEUR DE TEMPS : quatre points, celui du temps courant allumé.
    const li = Math.min(LIGNES - 1, l0 + haut + INDICATEUR_SOUS);
    const largeInd = (INDICATEUR_POINTS - 1) * INDICATEUR_ECART + 1;
    const ci = Math.round((this._colonnes - largeInd) / 2);
    for (let k = 0; k < INDICATEUR_POINTS; k++) {
      this._poserCase(ci + k * INDICATEUR_ECART, li,
        k === this._tempsDansMesure - 1 ? INDICATEUR_ALLUME : INDICATEUR_ETEINT, false);
    }
  }

  dispose() {
    this._scene.remove(this._fond);
    this._fondGeo.dispose(); this._fondMat.dispose();
    this._scene.remove(this._points);
    this._pointGeo.dispose(); this._pointMat.dispose();
    if (this._explosion) {
      this._scene.remove(this._explosion);
      this._expGeo.dispose(); this._expMat.dispose();
    }
  }
}
